import {
  action,
  computed,
  decorate,
  observable,
  runInAction,
} from 'mobx';
import { Bind } from 'lodash-decorators';
import BlogService from 'services/Blog.service';

import { BLOG_SETTINGS, DEFAULT_BLOG_FILTER } from 'common/consts/blog';

/* Disable ESlint rules for this file */
/* eslint no-console: "off" */
/* eslint class-methods-use-this: "off" */

class BlogStore {
  latestArticle = {};

  articlesDetails = [];

  blogState = {
    activeCategory: this.defaultBlogFilter,
    loadedArticlesCount: BLOG_SETTINGS.LISTING.DEFAULT_LIMIT,
    loadedArticlesInArticleView: BLOG_SETTINGS.ARTICLE.DEFAULT_LIMIT,
  };

  initialFetchDone = observable.box(false);

  constructor(coreStore) {
    this.coreStore = coreStore;
  }

  @computed
  get isLoaded() {
    return this.initialFetchDone.get();
  }

  // articlesDetails array with removed first item (duplicate)
  @computed
  get listingArticles() {
    return this.articlesDetails.slice(1, this.articlesDetails.length);
  }

  @Bind()
  getArticlesDetailsForArticleView(activeArticleId) {
    const uniqueArticles = this.articlesDetails.filter(article => article.id !== activeArticleId);
    return uniqueArticles.slice(0, this.blogState.loadedArticlesInArticleView);
  }

  @computed
  get allArticlesInArticleViewLoaded() {
    return this.articlesDetails.length - 1 <= this.blogState.loadedArticlesInArticleView;
  }

  @action
  setBlogFilter(codename, isAuthor = false) {
    let activeCategory;
    if (!isAuthor) {
      activeCategory = this.categoriesToRender
        .find(category => category.value === codename);
    } else {
      activeCategory = this.blogAuthors
        .find(author => author.value === codename);
    }
    this.blogState.activeCategory = activeCategory;
  }

  @action
  setDefaultBlogFilter() {
    this.blogState.activeCategory = this.defaultBlogFilter;
  }

  @computed
  get defaultBlogFilter() {
    const filter = DEFAULT_BLOG_FILTER;
    filter.count = this.articlesLength;
    return filter;
  }

  @Bind
  translateBlogCategory(categoryCodeName) {
    if (this.coreStore.translations.blogCategoryTranslations) {
      const categoryTranslation = this.coreStore.translations.blogCategoryTranslations
        .find(translation => translation.codeName === categoryCodeName);
      return categoryTranslation ? categoryTranslation.category : '';
    }
    return '';
  }

  @computed
  get articlesLength() {
    return this.articlesDetails.length;
  }

  async initialContentFetch() {
    if (this.isLoaded) {
      return null;
    }

    try {
      const [articlesDetails, latestArticle] = await Promise.all([
        BlogService.fetchArticlesDetails(),
        this.fetchLatestArticle(),
      ]);

      runInAction(() => {
        this.articlesDetails.push(...articlesDetails.items);
        this.latestArticle = latestArticle.firstItem;
        this.initialFetchDone.set(true);
        this.setDefaultBlogFilter();
      });
      return this.content;
    } catch (e) {
      console.error('Failed to fetch blog data', e);
      return null;
    }
  }

  async fetchLatestArticle() {
    let latestArticle = await BlogService.fetchLatestArticle();

    // If there is no promoted article, fetch latest one instead
    if (!latestArticle.items.length) {
      const fallback = true;
      latestArticle = await BlogService.fetchLatestArticle(fallback);
    }

    return latestArticle;
  }

  @computed
  get categoriesToRender() {
    // const uniqueProductNames = uniqBy(flattenDeep(this.productNames), 'value');
    // return [DEFAULT_BLOG_FILTER, ...uniqueProductNames, ...this.blogAuthors];
    return [this.defaultBlogFilter, ...this.categoriesUsedForArticles];
  }

  @computed
  get productNames() {
    return this.articlesDetails.reduce((products, articleDetails) => {
      const articleProducts = articleDetails.relatedProducts;
      const productsToReturn = [];

      articleProducts.forEach((articleProduct) => {
        const existingProduct = products.find(product => product.name === articleProduct.name);
        if (existingProduct) {
          existingProduct.count += 1;
        } else {
          const newProduct = articleProduct;
          newProduct.count = 1;
          newProduct.value = newProduct.system.codename;
          productsToReturn.push(newProduct);
        }
      });

      return [...products, ...productsToReturn];
    }, []);
  }

  @computed
  get blogAuthors() {
    return this.articlesDetails.reduce((authors, articleDetails) => {
      let currentAuthor = authors.find(author => author.name === articleDetails.articleAuthor.name);

      if (currentAuthor) {
        currentAuthor.count += 1;
        return authors;
      }

      currentAuthor = articleDetails.articleAuthor;
      if (currentAuthor && currentAuthor.system) {
        currentAuthor.count = 1;
        currentAuthor.value = currentAuthor.system.codename;
        return [...authors, currentAuthor];
      }

      return authors;
    }, []);
  }

  @computed
  get categoriesUsedForArticles() {
    if (this.coreStore.translations.blogCategoryTranslations) {
      return this.articlesDetails.reduce((categories, articleDetails) => {
        if (articleDetails.system.id === this.latestArticle.system.id) {
          return categories;
        }

        let currentCategory;
        articleDetails.categories.forEach((articleCategory) => {
          currentCategory = categories
            .find(category => articleCategory.codename === category.value);

          if (currentCategory) {
            currentCategory.count += 1;
            return categories;
          }

          currentCategory = articleCategory;
          if (currentCategory && currentCategory.codename) {
            const category = {
              count: 1,
              value: currentCategory.codename,
              name: this.translateBlogCategory(currentCategory.codename),
            };
            categories = [...categories, category];
          }
        });
        return categories;
      }, []);
    }
    return [];
  }

  @computed
  get loadArticles() {
    const { activeCategory, loadedArticlesCount } = this.blogState;

    if (!activeCategory) {
      return;
    }

    if (activeCategory.value === '') {
      return this.listingArticles.slice(0, loadedArticlesCount);
    }
    // Check if filtering is by authors
    const filterByAuthors = !!this.blogAuthors
      .filter(authors => authors.value === activeCategory.value).length;
    const filteredArticles = filterByAuthors ? this.filterByAuthor : this.filterByCategory;

    return filteredArticles.slice(0, loadedArticlesCount);
  }


  @computed
  get filterByCategory() {
    return this.listingArticles
      .filter(article => (article.categories.some(category => category.codename === this.blogState.activeCategory.value)));
  }

  @computed
  get filterByAuthor() {
    return this.listingArticles
      .filter(article => (article.articleAuthor.codename === this.blogState.activeCategory.value));
  }

  @computed
  get articlesCount() {
    return this.listingArticles
      .filter(article => (article.articleAuthor.codename === this.blogState.activeCategory.value));
  }

  async getArticleBySlug(slug) {
    if (this.isLoaded && this.latestArticle.slug === slug) {
      return this.latestArticle;
    }

    try {
      const article = await BlogService.getArticleBySlug(slug);
      return article.isEmpty ? null : article.items[0];
    } catch (e) {
      console.error('Failed to fetch article by slug');
      return null;
    }
  }

  @action
  setArticlesLimitInListingView(isBottom) {
    if (isBottom && this.isLoaded) {
      this.blogState.loadedArticlesCount += BLOG_SETTINGS.LISTING.LOAD_ARTICLES_COUNT;
    }
  }

  @action
  resetArticlesCountinArticleView() {
    this.blogState.loadedArticlesInArticleView = BLOG_SETTINGS.ARTICLE.DEFAULT_LIMIT;
  }

  @Bind()
  @action
  loadMoreArticlesinArticleView() {
    this.blogState.loadedArticlesInArticleView += BLOG_SETTINGS.ARTICLE.LOAD_ARTICLES_COUNT;
  }
}

decorate(BlogStore, {
  content: observable,
  latestArticle: observable,
  articlesDetails: observable,
  blogState: observable,
  initialContentFetch: action,
});

export default BlogStore;



// WEBPACK FOOTER //
// ./src/stores/Blog.store.js